import api, { fetchContactDetails, fetchOurOffices } from "api";
import { Crumbs } from "molecules/Breadcrumbs";

import { TitleHeading } from "molecules/TitleHeading";
import React, { useEffect, useMemo, useState } from "react";
import { Image } from "primereact/image";
import { SmallHadding } from "molecules/SmallHeadding";
import { DetailBox } from "molecules/DetailBox";
import Divider from "molecules/SimpleBorder/Divider";
import { useSelector } from "react-redux";

function buildContactItems(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const peoples = [];
    [
      [row.name1, row.email1],
      [row.name2, row.email2],
      [row.name3, row.email3],
    ].forEach(([name, email]) => {
      const n = name != null ? String(name).trim() : "";
      const e = email != null ? String(email).trim() : "";
      if (n || e) {
        peoples.push({ name: n || "—", email: e });
      }
    });
    const title = row.title || "";
    return {
      id: row.id,
      title,
      titleMobile: title.replace(/<br\s*\/?>/gi, " "),
      peoples,
    };
  });
}

function officeImageSrc(image) {
  if (!image || !String(image).trim()) return "/media/map-img.png";
  const s = String(image).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const root = (api.defaults.baseURL || "").replace(/\/$/, "");
  return `${root}/uploads/${s.replace(/^\/+/, "")}`;
}

const ContactUs = () => {
  const { data } = useSelector((state) => state.dashboard);
  const [contactItems, setContactItems] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(true);
  const [extrasError, setExtrasError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingExtras(true);
      setExtrasError(null);
      try {
        const [details, officeList] = await Promise.all([
          fetchContactDetails(),
          fetchOurOffices(),
        ]);
        if (cancelled) return;
        setContactItems(buildContactItems(details));
        setOffices(Array.isArray(officeList) ? officeList : []);
      } catch (e) {
        if (!cancelled) {
          setExtrasError(e?.message || "Could not load contact sections.");
          setContactItems([]);
          setOffices([]);
        }
      } finally {
        if (!cancelled) setLoadingExtras(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasOffices = offices.length > 0;

  const visibleContacts = useMemo(
    () => contactItems.filter((item) => item.peoples.length > 0),
    [contactItems]
  );
  const hasContacts = visibleContacts.length > 0;

  return (
    <div className="backgroundThm mobileContainer">
      <section>
        <div className="container  pl-0 pr-0">
          <Crumbs />
          <div className="grid section-title">
            <div
              className="lg:col-4 md:col-3 sm:col-12   pl-0"
              data-aos="fade-up"
              data-aos-duration="1500"
            >
              <h1 className="pageTitle ">Contact-us</h1>
            </div>
          </div>
          <div className="my-2 border-top-1 border-bottom-1 py-3 border-black-600 flex ">
            <div style={{ backgroundColor: "#4E6766" }} className="w-full">
              <div className="grid p-5 align-items-center">
                <div className="lg:col-5 md:col-6 sm:col-12 text-white">
                  <SmallHadding
                    text="Quantum Realty Headquarters"
                    className="mb-4 text-6xl eb-garamond-font"
                  />

                  <div className="flex align-items-center text-base mb-4">
                    <DetailBox
                      details={data?.contact_us_address}
                      className="text-white w-19rem"
                    />
                  </div>
                  <div className="flex align-items-center text-sm mb-4">
                    <i className="pi pi-phone mr-2 mb-1"></i>
                    <span className="text-xl mb-1">
                      {data?.contact_us_mobile_number}
                    </span>
                  </div>

                  <div className="flex align-items-center text-sm ">
                    <i className="pi pi-envelope mr-2"></i>
                    <span className="text-xl">{data?.contact_us_email}</span>
                  </div>
                </div>

                <div className="lg:col-7 md:col-6 sm:col-12">
                  <Image
                    src={data?.contact_us_banner_image}
                    alt="Image"
                    width="100%"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section>
        {loadingExtras && (
          <div className="container pl-0 pr-0 py-3 text-center text-600">
            Loading contact listings…
          </div>
        )}
        {extrasError && !loadingExtras && (
          <div className="container pl-0 pr-0 py-3 text-center text-red-500">
            {extrasError}
          </div>
        )}
        <div className="container contactUsDesktop  pl-0 pr-0 last-border-none">
          {!loadingExtras &&
            visibleContacts.map((item) => (
              <div
                key={item.id}
                data-aos="fade-up "
                className="grid  border-bottom-1   contactUs  py-3 "
                style={{}}
              >
                <div
                  className="col-4 pt-2  pl-0 text-xl "
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  <h3
                    className={`section-title p-0 contactFont`}
                    dangerouslySetInnerHTML={{ __html: item.title }}
                  />
                </div>
                <div
                  className="pl-0 col-6 d-flex align-items-start justify-content-center flex-column"
                  style={{ gap: "15px" }}
                >
                  {item.peoples.map((p, idx) => (
                    <div
                      key={`${item.id}-${idx}`}
                      className="d-flex mt-1 align-items-start justify-content-center flex-column "
                    >
                      <p
                        style={{ fontSize: "1.3rem" }}
                        className="mb-0 text-black-alpha-90"
                      >
                        {p?.name}
                      </p>
                      {p?.email ? (
                        <a
                          href={`mailto:${p.email}`}
                          className="fs-17  font-medium  "
                          style={{ color: "#C82027", margin: 0 }}
                        >
                          {p.email}
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          {!loadingExtras && !hasContacts && !extrasError && (
            <div className="py-3 text-600">No department contacts to show.</div>
          )}
        </div>
        <div className="container contactUsMobile pl-0 pr-0 last-border-none">
          {!loadingExtras &&
            visibleContacts.map((item) => (
              <div
                key={`m-${item.id}`}
                data-aos="fade-up "
                className="grid  border-bottom-1   contactUs  py-3 "
                style={{}}
              >
                <div
                  className="col-4 pt-2  pl-0 text-xl  w-100"
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  <h3 className="section-title mb-0 p-0 contactFont ">
                    {item.titleMobile}
                  </h3>
                </div>
                <div
                  className="pl-0 col-6 d-flex align-items-start justify-content-center flex-column"
                  style={{ gap: "15px" }}
                >
                  {item.peoples.map((p, idx) => (
                    <div
                      key={`${item.id}-m-${idx}`}
                      className="d-flex mt-1 align-items-start justify-content-center flex-column "
                    >
                      <p
                        style={{ fontSize: "1.3rem" }}
                        className="mb-0 text-black-alpha-90"
                      >
                        {p?.name}
                      </p>
                      {p?.email ? (
                        <a
                          href={`mailto:${p.email}`}
                          className="fs-17  font-medium  "
                          style={{ color: "#C82027", margin: 0 }}
                        >
                          {p.email}
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </section>
      <div className="container pl-0 pr-0">
        <Divider />
      </div>
      <div className="container pl-0 pr-0">
        <section className="section-padding px-0">
          <div className="container  pl-0 pr-0 last-border-none">
            <div className="lg:col-9 md:col-8 sm:col-12 p-0 ">
              <TitleHeading title="Our offices" className="mt-0 mb-3 " />
            </div>

            <div className="grid mt-4">
              {!loadingExtras &&
                hasOffices &&
                offices.map((cData) => (
                  <div
                    key={cData.id}
                    data-aos="fade-up"
                    className="lg:col-3 md:col-6 sm:col-12 p-0  "
                  >
                    <img
                      src={officeImageSrc(cData.image)}
                      alt=""
                      className="img-fluid "
                    />

                    <h5
                      style={{
                        fontWeight: "100 !important",
                        textTransform: "capitalize",
                        color: "#424242",
                        fontSize: "23px !importent",
                      }}
                      className="text-left   mt-2 mb-0"
                    >
                      {cData?.city}
                    </h5>
                    <p className="mt-1">
                      {cData?.address}
                      <br />
                      {cData?.pincode}
                    </p>
                    <p></p>
                  </div>
                ))}
              {!loadingExtras && !hasOffices && !extrasError && (
                <div className="col-12 text-600">No offices to show.</div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactUs;
